import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  ElementRef,
  OnDestroy,
  OnInit,
  computed,
  inject,
  signal,
} from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { TranslocoDirective, TranslocoService } from '@jsverse/transloco';

import { ContextService } from '../../core/context.service';
import { LanguageService } from '../../core/i18n/language.service';
import { ThemeService } from '../../core/theme/theme.service';
import { Select, SelectOption } from '../../shared/components/forms/select';
import { ToastService } from '../../shared/services/toast-service';
import { UserSetting, UserSettingSource } from './models/tenant-setting.model';
import { UserPreferencesService } from './services/user-preferences.service';
import { SettingRow } from './setting-row.component';
import { SettingSection } from './setting-section.component';
import { LOCALE_OPTIONS, TIMEZONE_OPTIONS, localeLabel } from './settings-options';
import { Button } from '../../shared/components/button/button';

type PrefSectionId = 'regional' | 'appearance';

const PREF_SECTIONS: PrefSectionId[] = ['regional', 'appearance'];

/** How far below the scroll container's top a section counts as "current". */
const SPY_OFFSET = 140;

const PREF_KEY = {
  locale: 'locale',
  timezone: 'timezone',
  theme: 'ui.theme',
  page: 'ui.defaultPage',
  pageSize: 'ui.tablePageSize',
} as const;

const KEY_SECTION: Record<string, PrefSectionId> = {
  [PREF_KEY.locale]: 'regional',
  [PREF_KEY.timezone]: 'regional',
  [PREF_KEY.theme]: 'appearance',
  [PREF_KEY.page]: 'appearance',
  [PREF_KEY.pageSize]: 'appearance',
};


/**
 * Personal preferences page (the "My preferences" screen of the Tenant
 * Settings design), backed by /api/v1/users/me/setting. Unlike the tenant page
 * there is no save bar: every control change is persisted immediately
 * (optimistic update, revert-and-toast on failure). locale/timezone are
 * USER_OVERRIDABLE (three-layer cascade, "Use tenant default" clears the user
 * layer); the ui.* keys are USER_ONLY (two layers, plain reset to default).
 *
 * ui.theme is persisted server-side and additionally applied locally through
 * ThemeService for light/dark picks. "system" is stored but not locally
 * applied — ThemeService intentionally doesn't follow prefers-color-scheme,
 * and reconciling the stored preference with localStorage at boot is a
 * separate integration decision.
 */
@Component({
  selector: 'app-user-preferences',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    Button,FormsModule, TranslocoDirective, Select, SettingRow, SettingSection],
  templateUrl: './user-preferences.component.html',
})
export class UserPreferencesComponent implements OnInit, OnDestroy {
  private readonly service = inject(UserPreferencesService);
  private readonly context = inject(ContextService);
  private readonly toast = inject(ToastService);
  private readonly transloco = inject(TranslocoService);
  private readonly languageService = inject(LanguageService);
  private readonly themeService = inject(ThemeService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly host = inject(ElementRef<HTMLElement>);

  protected readonly prefSections = PREF_SECTIONS;
  protected readonly localeOptions = LOCALE_OPTIONS;
  protected readonly timezoneOptions = TIMEZONE_OPTIONS;
  protected readonly themeOptions = [
    { value: 'system', labelKey: 'userPreferences.appearance.theme.system' },
    { value: 'light', labelKey: 'userPreferences.appearance.theme.light' },
    { value: 'dark', labelKey: 'userPreferences.appearance.theme.dark' },
  ] as const;

  protected readonly loading = signal(true);
  protected readonly loadFailed = signal(false);
  private readonly settings = signal<UserSetting[]>([]);
  /** Key with a write in flight; its control is disabled meanwhile. */
  protected readonly busyKey = signal<string | null>(null);
  protected readonly savedNotes = signal<Partial<Record<PrefSectionId, true>>>({});
  protected readonly activeSection = signal<PrefSectionId>('regional');

  private spyLocked = false;
  private spyUnlockTimer?: ReturnType<typeof setTimeout>;

  private readonly organization = toSignal(this.context.organization$, { initialValue: null });
  private readonly user = toSignal(this.context.user$, { initialValue: null });
  protected readonly tenantName = computed(() => this.organization()?.name ?? '');
  protected readonly userName = computed(() => {
    const u = this.user();
    if (!u) return '';
    return [u.firstName, u.lastName].filter(Boolean).join(' ');
  });

  private readonly byKey = computed(() => new Map(this.settings().map((s) => [s.key, s])));

  /** Typed effective values + cascade source for the five preference controls. */
  protected readonly vm = computed(() => {
    const map = this.byKey();
    const pick = (key: string) => {
      const s = map.get(key);
      return { value: s?.value, source: (s?.source ?? null) as UserSettingSource | null };
    };
    const str = (v: unknown, fallback: string) => (typeof v === 'string' ? v : fallback);
    const locale = pick(PREF_KEY.locale);
    const timezone = pick(PREF_KEY.timezone);
    const theme = pick(PREF_KEY.theme);
    const page = pick(PREF_KEY.page);
    const pageSize = pick(PREF_KEY.pageSize);
    return {
      locale: { value: str(locale.value, 'en-GB'), source: locale.source },
      timezone: { value: str(timezone.value, 'UTC'), source: timezone.source },
      theme: { value: str(theme.value, 'system'), source: theme.source },
      page: { value: str(page.value, 'fleet'), source: page.source },
      pageSize: {
        value: typeof pageSize.value === 'number' ? pageSize.value : 20,
        source: pageSize.source,
      },
    };
  });

  protected readonly pageOptions = computed<SelectOption[]>(() => {
    this.languageService.current(); // re-evaluate on language switch
    const t = (k: string) => this.transloco.translate<string>(`userPreferences.appearance.page.${k}`);
    return [
      { value: 'fleet', label: t('fleet') },
      { value: 'devices', label: t('devices') },
      { value: 'files', label: t('files') },
      { value: 'layouts', label: t('layouts') },
    ];
  });

  protected readonly pageSizeOptions = computed<SelectOption[]>(() => {
    this.languageService.current(); // re-evaluate on language switch
    return [10, 20, 50].map((n) => ({
      value: n,
      label: this.transloco.translate<string>('userPreferences.appearance.pageSize.rows', { count: n }),
    }));
  });

  /** Cascade captions per row ("Using tenant default (…)", "Default: …"). */
  protected readonly captions = computed(() => {
    this.languageService.current(); // re-evaluate on language switch
    const map = this.byKey();
    const t = (key: string, params?: Record<string, unknown>) =>
      this.transloco.translate<string>(key, params);

    // locale/timezone: name the layer currently in effect. When the user is
    // overriding, the underlying tenant value isn't in the payload, so the
    // caption stays valueless.
    const cascade = (key: string, label: (v: unknown) => string) => {
      const s = map.get(key);
      if (!s) return '';
      if (s.source === 'USER') return t('userPreferences.overridingTenantDefault');
      return s.source === 'TENANT'
        ? t('userPreferences.usingTenantDefault', { value: label(s.value) })
        : t('userPreferences.usingPlatformDefault', { value: label(s.value) });
    };
    const userOnly = (key: string, label: (v: unknown) => string) => {
      const s = map.get(key);
      return s ? t('userPreferences.default', { value: label(s.defaultValue) }) : '';
    };
    const asStr = (v: unknown) => (typeof v === 'string' ? v : String(v ?? ''));
    const themeLabel = (v: unknown) => {
      const opt = this.themeOptions.find((o) => o.value === v);
      return opt ? t(opt.labelKey) : asStr(v);
    };
    const pageLabel = (v: unknown) =>
      this.pageOptions().find((o) => o.value === v)?.label ?? asStr(v);
    const sizeLabel = (v: unknown) =>
      typeof v === 'number' ? t('userPreferences.appearance.pageSize.rows', { count: v }) : asStr(v);

    return {
      locale: cascade(PREF_KEY.locale, (v) => localeLabel(asStr(v))),
      timezone: cascade(PREF_KEY.timezone, asStr),
      theme: userOnly(PREF_KEY.theme, themeLabel),
      page: userOnly(PREF_KEY.page, pageLabel),
      pageSize: userOnly(PREF_KEY.pageSize, sizeLabel),
    };
  });

  private readonly savedTimers = new Map<PrefSectionId, ReturnType<typeof setTimeout>>();

  ngOnInit(): void {
    this.fetch();
    window.addEventListener('scroll', this.onAnyScroll, { capture: true, passive: true });
  }

  ngOnDestroy(): void {
    window.removeEventListener('scroll', this.onAnyScroll, { capture: true });
    clearTimeout(this.spyUnlockTimer);
    this.savedTimers.forEach((timer) => clearTimeout(timer));
  }

  protected retry(): void {
    this.fetch();
  }

  /** Persists one preference immediately; reverts to server state on failure. */
  protected setPref(key: string, value: unknown): void {
    if (this.busyKey() !== null) return;
    const current = this.byKey().get(key);
    if (current && current.source === 'USER' && current.value === value) return;
    // Optimistic: the control reflects the pick instantly.
    this.settings.update((list) =>
      list.map((s) => (s.key === key ? { ...s, value, source: 'USER' as const } : s)),
    );
    this.busyKey.set(key);
    this.service
      .update(key, value)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.busyKey.set(null);
          this.applySideEffects(key, value);
          this.flashSaved(KEY_SECTION[key]);
        },
        error: (err: unknown) => {
          this.busyKey.set(null);
          const apiMessage = (err as { error?: { message?: string } })?.error?.message;
          this.toast.show(
            apiMessage || this.transloco.translate<string>('userPreferences.toast.saveFailed'),
            'error',
          );
          this.fetch(true); // revert the optimistic value
        },
      });
  }

  /** Removes the user override; refetches because the fallback value (tenant
   *  layer for locale/timezone) isn't known client-side. */
  protected clearPref(key: string): void {
    if (this.busyKey() !== null) return;
    this.busyKey.set(key);
    this.service
      .reset(key)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.busyKey.set(null);
          this.flashSaved(KEY_SECTION[key]);
          this.fetch(true);
        },
        error: (err: unknown) => {
          this.busyKey.set(null);
          const apiMessage = (err as { error?: { message?: string } })?.error?.message;
          this.toast.show(
            apiMessage || this.transloco.translate<string>('userPreferences.toast.saveFailed'),
            'error',
          );
        },
      });
  }

  protected setTheme(value: string): void {
    this.setPref(PREF_KEY.theme, value);
  }

  protected goTo(id: PrefSectionId): void {
    const el = document.getElementById('sec-' + id);
    if (!el) return;
    this.activeSection.set(id);
    this.spyLocked = true;
    clearTimeout(this.spyUnlockTimer);
    this.spyUnlockTimer = setTimeout(() => (this.spyLocked = false), 700);
    el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  private readonly onAnyScroll = (event: Event): void => {
    if (this.spyLocked) return;
    const container = event.target;
    if (!(container instanceof HTMLElement) || !container.contains(this.host.nativeElement)) {
      return;
    }
    const containerTop = container.getBoundingClientRect().top;
    let active: PrefSectionId = PREF_SECTIONS[0];
    for (const id of PREF_SECTIONS) {
      const el = document.getElementById('sec-' + id);
      if (el && el.getBoundingClientRect().top - containerTop <= SPY_OFFSET) active = id;
    }
    if (container.scrollTop + container.clientHeight >= container.scrollHeight - 8) {
      active = PREF_SECTIONS[PREF_SECTIONS.length - 1];
    }
    if (active !== this.activeSection()) this.activeSection.set(active);
  };

  private applySideEffects(key: string, value: unknown): void {
    if (key === PREF_KEY.theme && (value === 'light' || value === 'dark')) {
      this.themeService.setTheme(value);
    }
    if (key === PREF_KEY.locale && typeof value === 'string') {
      // BCP-47 locale (e.g. 'sl-SI') → Transloco lang id ('sl').
      const lang = value.split('-')[0];
      this.languageService.setLanguage(lang);
    }
  }

  private fetch(quiet = false): void {
    if (!quiet) {
      this.loading.set(true);
      this.loadFailed.set(false);
    }
    this.service
      .getAll()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res) => {
          this.settings.set(res.data);
          this.loading.set(false);
        },
        error: () => {
          this.loading.set(false);
          if (!quiet) this.loadFailed.set(true);
          this.toast.show(
            this.transloco.translate<string>('userPreferences.toast.loadFailed'),
            'error',
          );
        },
      });
  }

  private flashSaved(id: PrefSectionId): void {
    this.savedNotes.update((n) => ({ ...n, [id]: true as const }));
    clearTimeout(this.savedTimers.get(id));
    this.savedTimers.set(
      id,
      setTimeout(() => {
        this.savedNotes.update((n) => {
          const next = { ...n };
          delete next[id];
          return next;
        });
      }, 2400),
    );
  }
}
